/**
 * "Things to Know Before Your Appointment" — a 50/50 split section: a lifestyle photo on
 * the left and an eggshell panel on the right with a heading and a list of prep-guidance
 * rows (pink-circle icon + title + body), sourced from the Starbase service.thingsToKnow
 * field ([{ title, body }]). Styled to match the Figma design (node 1491:4755).
 */
/* eslint-disable @next/next/no-img-element */
import React from 'react'
import Image from 'next/image'
import styled from '@emotion/styled'
import { gsColors as c } from '../../styles/Variables'
import { mq } from '../../helpers'

const ROW_ICON = '/service-detail/things-icon.svg'

/**
 * @param {{ items?: Array<{ title: string, body: string }> }} props
 */
const ThingsToKnow = ({ items = [] }) => {
  const list = Array.isArray(items) ? items : []
  if (!list.length) {
    return null
  }
  return (
    <Wrapper>
      <div className="image">
        <Image
          src="/service-detail/things-to-know.jpg"
          alt="A GLAMSQUAD client having her hair styled at home"
          fill
          sizes="(max-width: 900px) 100vw, 50vw"
          style={{ objectFit: 'cover' }}
        />
      </div>
      <div className="panel">
        <div className="content">
          <h2>Things to Know Before Your Appointment</h2>
          <ul>
            {list.map((item, i) => (
              <li key={`${item.title}-${i}`}>
                <img src={ROW_ICON} alt="" aria-hidden="true" width={48} height={48} />
                <div className="text">
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: stretch;

  ${mq('max', '900px')} {
    grid-template-columns: minmax(0, 1fr);
  }

  .image {
    position: relative;
    background: #c8c8cc;
    min-height: 668px;
    ${mq('max', '900px')} {
      min-height: 0;
      aspect-ratio: 4 / 3;
    }
  }

  .panel {
    background: ${c.eggshell};
    display: flex;
    align-items: center;
    padding: 80px 88px;
    ${mq('max', '900px')} {
      padding: 48px 24px;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  h2 {
    font-family: Garnett;
    font-size: 32px;
    font-weight: 600;
    line-height: 1.25;
    color: ${c.black};
    margin: 0 0 32px;
    ${mq('max', '900px')} {
      font-size: 24px;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  li {
    display: flex;
    align-items: center;
    gap: 18px;
  }

  li img {
    flex-shrink: 0;
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  h3 {
    font-family: Euclid;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.45;
    color: ${c.black};
    margin: 0;
    ${mq('max', '900px')} {
      font-size: 18px;
    }
  }

  p {
    font-family: Euclid;
    font-size: 16px;
    line-height: 1.45;
    color: ${c.darkGrey};
    margin: 0;
  }
`

export default ThingsToKnow
